import React, { useCallback, useMemo, useState } from "react";
import { useSnackbar } from "notistack";

import { Add } from "@mui/icons-material";
import ScreenActions from "@dyl/components/ScreenActions";
import { Fab, Grid, Typography } from "@mui/material";
import LinkButton from "@dyl/components/LinkButton";
import quickDateFormat from "@dyl/utils/quickDateFormat";
import GenericDeleteAction from "@dyl/components/GenericDeleteAction";
import useChangeEvent from "@dyl/hooks/useChangeEvent";
import SearchField from "@dyl/components/SearchField";
import getKeywords from "@dyl/apiClients/backendApi/getKeywords";
import deleteKeyword from "@dyl/apiClients/backendApi/deleteKeyword";
import useAsync from "@dyl/hooks/useAsync";
import { useTitle } from "@dyl/systems/TitleManager";
import DataGridDyl from "@dyl/components/DataGridDyl";
import keywordFormat from "@dyl/utils/keywordFormat";
import KeywordModel from "@dyl/components/MisspellingKeywords/KeywordModel";
import ActivityLogs from "@dyl/components/ActivityLogs";
import { useAuth } from "@dyl/systems/UserManager";
import SectionDividers from '@dyl/components/SectionDividers';
import useStyles from './styles';
import { useLevels } from "@dyl/systems/LevelsManager";

export default function KeywordList() {
  useTitle("Misspelling Keywords");
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [loading, setLoading] = useState(true);
  const [triggerReload, setTriggerReload] = useState(0);
  const [search, setSearch] = useState("");

  const [showKeywordModel, setShowKeywordModel] = useState(false);
  const [modelProps, setModelPros] = useState({
    open: false,
    keyword: "",
    misspellings: "",
    title:" ",
    mode: '',
    id: null
  });
  const { user } = useAuth();
  let username = user?.attributes?.email;
  const { keywords=[] } =
    useAsync(async () => {
      setLoading(true);

      try {
        return await getKeywords();
      } catch {
        enqueueSnackbar("Failed to load keywords", { variant: "error" });
      } finally {
        setLoading(false);
      }
    }, [triggerReload]) ?? [];

  const onSearch = useChangeEvent(setSearch);

  const columns = useMemo(() => {
    /** @type {import('@mui/x-data-grid').GridColDef[]} */
    const columns = [
      { headerName: "Keyword", field: "text", minWidth: 180 },
      {
        headerName: "Accepted misspellings",
        field: "misspellings",
        flex: 4,
        minWidth: 650,
        valueFormatter({ value }) {
          return keywordFormat(value);
        },
      },

      {
        headerName: "Last updated",
        field: "updated_at",
        minWidth: 150,
        valueFormatter({ value }) {
          return quickDateFormat(value);
        },
      },

      {
        headerName: "Delete",
        field: "ADMIN_DELETE",
        minWidth: 100,
        renderCell({ row }) {
          return (
            <GenericDeleteAction
              data={row}
              promptTexts={{
                dialogTitle: () => "Delete Keyword?",
                dialogText: (data) =>
                  `Are you sure you want to delete Keyword "${data.text}"?`,
                ongoingNotification: (data) =>
                  `Deleting Keyword "${data.text}"...`,
                successNotification: (data, response) =>
                  `Deleted Keyword "${data.text}".`,
                failureNotification: (data, response) =>
                  `Was unable to delete Keyword "${data.text}" for an unknown reason.`,
                errorNotification: (data) =>
                  `Failed to delete Keyword "${data.text}".`,
              }}
              deleteAction={(data) => deleteKeyword(data.id, username)}
              responseSuccessPredicate={(response) => response?.text}
              onDelete={() => setTriggerReload(prev => prev + 1)}
            />
          );
        },
      },
    ];

    return columns;
  }, []);

  const updateKeyword = useCallback(({ row }) => {
    console.log(row)
    setShowKeywordModel(true)
    setModelPros({
      keyword: row.text,
      misspellings: row.misspellings.join(', '),
      title:"Edit keyword or misspellings",
      mode: 'UPDATE',
      id: row.id
    })
  }, []);

  const handleAddNewKeyword = useCallback(()=>{
    setShowKeywordModel(true);
    setModelPros({
      keyword: "",
      misspellings: "",
      title:"Add new Keyword",
      mode: 'NEW_ADD',
      id: null
    })

  },[])

  const filterdRows = useMemo(() => {
    return keywords.filter(x =>
        x?.text?.toLowerCase()?.includes?.(search.toLowerCase()))
}, [keywords, search]);

  return (
    <>

      <FilterBlock search={search} onSearch={onSearch} />

      <DataGridDyl
        rows={(search.length > 0 ? filterdRows:  keywords) ?? []}
        columns={columns}
        onCellClick={updateKeyword}
        variant="links"
        loading={loading}
        getRowHeight={() => 'auto'}
      />

      <SectionDividers DividerProps={{ className: classes.divider }}>
        <ActivityLogs
          logId={'keywords'}
          logType='keywords'
          reload={triggerReload} />
      </SectionDividers>

      <ScreenActions>
        <LinkButton
          component={Fab}
          color="primary"
          variant="extended"
          size="large"
          onClick={handleAddNewKeyword}
        >
          <Add />
          &nbsp;New
        </LinkButton>
      </ScreenActions>
      { showKeywordModel && <KeywordModel
        keywords = {keywords}
        onClose={() => setShowKeywordModel(false)}
        open= { showKeywordModel }
        refreshTable= {()=> setTriggerReload(prev => prev + 1)}
        {...modelProps}
      /> }
    </>
  );
}

function FilterBlock({ search, onSearch }) {
  const { globalLevel } = useLevels();
  return (
    <Grid container alignItems="center" spacing={4}>
      <Grid item md={6}>
        {globalLevel?.label === 'A1' ? (
          <Typography variant="body2" style={{ marginBottom: '20px' }}>
            Note that this list is currently only used for the last two A1 Comic Story items (7 and 8).
          </Typography>
        ) : (
          <div style={{ marginBottom: '20px' }} />
        )}
        <SearchField fullWidth value={search} onChange={onSearch} />
      </Grid>
    </Grid>
  );
}